import {
  Box, BreadcrumbGroup, Button, ButtonDropdown, ColumnLayout, ExpandableSection, Flashbar,
  FlashbarProps, Header, KeyValuePairs, Modal, SpaceBetween, Spinner, StatusIndicator, Table, Toggle,
} from "@cloudscape-design/components";
import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { getAuthToken } from "./auth";
import { getAwsCreds, getDefaultRegion } from "./awsCredentials";
import { apiFetch } from "./apiFetch";
import { useDocumentTitle } from "./useDocumentTitle";
import EcsServiceDetail, { EcsServiceData } from "./EcsServiceDetail";
import Ec2InstanceDetail, { Ec2InstanceData } from "./Ec2InstanceDetail";
import DocumentationModal from "./DocumentationModal";
import EcsLogsModal from "./EcsLogsModal";
import ConfirmActionModal from "./ConfirmActionModal";
import { scaleEcsService } from "./ecsActions";
import {
  ec2InstanceUrl, ecsServiceUrl, lambdaFunctionUrl, rdsInstanceUrl, apiGatewayUrl, dynamoDbTableUrl,
} from "./awsConsoleUrls";

interface DocItem { documentId: string; title: string; }

const PLURAL_MAP: Record<string, string> = {
  "ec2-instance": "ec2-instances",
  "lambda-function": "lambda-functions",
  "api-gateway": "api-gateways",
  "ecs-service": "ecs-services",
  "rds-instance": "rds-instances",
  "dynamodb-table": "dynamodb-tables",
  "fm-system-defined-inference-profile": "fm-system-defined-inference-profiles",
};

const DOC_ENDPOINTS: Record<string, string> = {
  "ec2-instance": "ec2-documentations",
  "ecs-service": "ecs-documentations",
};

const TYPE_LABELS: Record<string, string> = {
  "ec2-instance": "EC2 Instance",
  "lambda-function": "Lambda Function",
  "api-gateway": "API Gateway",
  "ecs-service": "ECS Service",
  "rds-instance": "RDS Instance",
  "dynamodb-table": "DynamoDB Table",
  "fm-system-defined-inference-profile": "FM System Defined Inference Profile",
};

function statusType(s: string): "success" | "error" | "warning" | "info" {
  if (/active|running|available/i.test(s)) return "success";
  if (/stopped|failed|error/i.test(s)) return "error";
  if (/pending|creating|updating/i.test(s)) return "warning";
  return "info";
}

export default function InventoryDetailPage() {
  const { assetType, id } = useParams<{ assetType: string; id: string }>();
  const navigate = useNavigate();
  const apiKey = getAuthToken();

  const [asset, setAsset] = useState<any>(null);
  useDocumentTitle(asset ? (asset.name || asset.resourceId) : null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resyncing, setResyncing] = useState(false);
  const [flash, setFlash] = useState<FlashbarProps.MessageDefinition[]>([]);
  const [ec2ModalVisible, setEc2ModalVisible] = useState(false);
  const [ecsLogsVisible, setEcsLogsVisible] = useState(false);

  // ECS Stop/Start confirmation flow (cached layout).
  const [ecsScaleAction, setEcsScaleAction] = useState<"stop" | "start" | null>(null);
  const [ecsScaleChecking, setEcsScaleChecking] = useState(false);
  const [ecsScaling, setEcsScaling] = useState(false);
  const [ecsFreshCounts, setEcsFreshCounts] = useState<{ desiredCount: number; runningCount: number; status: string } | null>(null);

  // Documentation dropdown + modal state (EC2 detail view)
  const [docs, setDocs] = useState<DocItem[]>([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [docModalVisible, setDocModalVisible] = useState(false);
  const [docTitle, setDocTitle] = useState<string | null>(null);
  const [docMarkdown, setDocMarkdown] = useState<string | null>(null);
  const [docLoading, setDocLoading] = useState(false);
  const [docError, setDocError] = useState<string | null>(null);

  const plural = assetType ? PLURAL_MAP[assetType] : null;

  useEffect(() => {
    if (!apiKey || !plural || !id) { setLoading(false); return; }
    apiFetch(`/${plural}/${id}?populate=*`)
      .then(r => { if (!r.ok) throw new Error(`API error: ${r.status}`); return r.json(); })
      .then(json => setAsset(json.data))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [apiKey, plural, id]);

  // Fetch enabled documentation list once the asset is loaded (any type in DOC_ENDPOINTS).
  useEffect(() => {
    const docEndpoint = assetType ? DOC_ENDPOINTS[assetType] : undefined;
    if (!docEndpoint || !asset?.documentId) return;
    setDocsLoading(true);
    apiFetch(`/${docEndpoint}?filters[enabled]=true&sort=order:asc&fields[0]=title`)
      .then(r => { if (!r.ok) throw new Error(`API error: ${r.status}`); return r.json(); })
      .then(json => setDocs(json.data ?? []))
      .catch(() => setDocs([]))
      .finally(() => setDocsLoading(false));
  }, [assetType, asset?.documentId]);

  // Render a selected documentation entry for the current asset type.
  const handleDocSelect = (itemId: string) => {
    const docEndpoint = assetType ? DOC_ENDPOINTS[assetType] : undefined;
    if (!docEndpoint || !asset?.documentId) return;
    setDocModalVisible(true);
    setDocTitle(null);
    setDocMarkdown(null);
    setDocError(null);
    setDocLoading(true);
    apiFetch(`/${docEndpoint}/${itemId}/render?resourceId=${asset.documentId}`)
      .then(r => { if (!r.ok) throw new Error(`API error: ${r.status}`); return r.json(); })
      .then((json: { title: string; rendered: string }) => {
        setDocTitle(json.title);
        setDocMarkdown(json.rendered);
      })
      .catch((e: Error) => setDocError(e.message || "Failed to load documentation"))
      .finally(() => setDocLoading(false));
  };

  const docItems = docs.length > 0
    ? docs.map((d) => ({ id: d.documentId, text: d.title }))
    : [{ id: "__none__", text: "No documentation available", disabled: true }];

  const handleResync = () => {
    if (!apiKey || !plural || !id || !assetType) return;
    const accountAlias = asset?.account?.Alias;
    if (accountAlias) {
      const creds = getAwsCreds(accountAlias);
      if (!creds) {
        setFlash([{ type: "warning", content: `No AWS credentials stored for "${accountAlias}". Configure them in Settings.`, dismissible: true, onDismiss: () => setFlash([]) }]);
        return;
      }
      setResyncing(true);
      const headers: Record<string, string> = {
        "X-AWS-Access-Key-Id": creds.accessKeyId,
        "X-AWS-Secret-Access-Key": creds.secretAccessKey,
        "X-AWS-Session-Token": creds.sessionToken,
      };
      apiFetch(`/inventory/${assetType}/${id}/resync`, { method: "POST", headers })
        .then(r => { if (!r.ok) throw new Error(`Re-sync failed: ${r.status}`); return r.json(); })
        .then(() => {
          setFlash([{ type: "success", content: "Asset re-synced successfully", dismissible: true, onDismiss: () => setFlash([]) }]);
          // Refresh asset data
          return apiFetch(`/${plural}/${id}?populate=*`);
        })
        .then(r => r && r.ok ? r.json() : null)
        .then(json => { if (json?.data) setAsset(json.data); })
        .catch(e => setFlash([{ type: "error", content: e.message, dismissible: true, onDismiss: () => setFlash([]) }]))
        .finally(() => setResyncing(false));
    } else {
      setFlash([{ type: "warning", content: "No account associated with this asset.", dismissible: true, onDismiss: () => setFlash([]) }]);
    }
  };

  // Open the Stop/Start confirmation modal for the cached ECS service and fire a
  // background resync so the modal reflects the current (live) desired/running
  // counts before the user confirms.
  const openEcsScale = async (action: "stop" | "start") => {
    setEcsScaleAction(action);
    setEcsFreshCounts(null);

    const accountAlias = asset?.account?.Alias;
    if (!accountAlias) {
      setEcsScaleAction(null);
      setFlash([{ type: "warning", content: "No account associated with this asset.", dismissible: true, onDismiss: () => setFlash([]) }]);
      return;
    }
    const creds = getAwsCreds(accountAlias);
    if (!creds) {
      setEcsScaleAction(null);
      setFlash([{ type: "warning", content: `No AWS credentials stored for "${accountAlias}". Configure them in Settings.`, dismissible: true, onDismiss: () => setFlash([]) }]);
      return;
    }
    if (!plural || !id || !assetType) return;

    setEcsScaleChecking(true);
    const headers: Record<string, string> = {
      "X-AWS-Access-Key-Id": creds.accessKeyId,
      "X-AWS-Secret-Access-Key": creds.secretAccessKey,
      "X-AWS-Session-Token": creds.sessionToken,
    };
    try {
      const resyncRes = await apiFetch(`/inventory/${assetType}/${id}/resync`, { method: "POST", headers });
      if (!resyncRes.ok) throw new Error(`Re-sync failed: ${resyncRes.status}`);
      await resyncRes.json().catch(() => null);

      const refreshRes = await apiFetch(`/${plural}/${id}?populate=*`);
      const json = refreshRes.ok ? await refreshRes.json() : null;
      if (json?.data) {
        setAsset(json.data);
        const dd = json.data.describeData ?? json.data.DescribeData ?? json.data.rawDescribe ?? null;
        const svc = dd ? (typeof dd === "string" ? JSON.parse(dd) : dd) : {};
        setEcsFreshCounts({
          desiredCount: svc.desiredCount ?? 0,
          runningCount: svc.runningCount ?? 0,
          status: svc.status ?? json.data.status ?? "—",
        });
      }
    } catch (e: any) {
      setFlash([{ type: "error", content: `Could not refresh current status: ${e.message}`, dismissible: true, onDismiss: () => setFlash([]) }]);
    } finally {
      setEcsScaleChecking(false);
    }
  };

  const confirmEcsScale = async (clusterName: string, serviceName: string, region: string) => {
    if (!ecsScaleAction || !plural || !id) return;
    const accountAlias = asset?.account?.Alias;
    const creds = accountAlias ? getAwsCreds(accountAlias) : null;
    if (!creds) {
      setEcsScaleAction(null);
      setFlash([{ type: "warning", content: `No AWS credentials stored for "${accountAlias}". Configure them in Settings.`, dismissible: true, onDismiss: () => setFlash([]) }]);
      return;
    }
    const desiredCount: 0 | 1 = ecsScaleAction === "start" ? 1 : 0;
    setEcsScaling(true);
    try {
      await scaleEcsService({ clusterName, serviceName, region, desiredCount, creds });
      setEcsScaleAction(null);
      setFlash([{
        type: "success",
        content: ecsScaleAction === "start"
          ? `Start requested for ${serviceName} (desired count set to 1).`
          : `Stop requested for ${serviceName} (desired count set to 0).`,
        dismissible: true,
        onDismiss: () => setFlash([]),
      }]);
      const refreshRes = await apiFetch(`/${plural}/${id}?populate=*`);
      const json = refreshRes.ok ? await refreshRes.json() : null;
      if (json?.data) setAsset(json.data);
    } catch (e: any) {
      setEcsScaleAction(null);
      setFlash([{ type: "error", content: e.message, dismissible: true, onDismiss: () => setFlash([]) }]);
    } finally {
      setEcsScaling(false);
    }
  };

  if (loading) return <Spinner size="large" />;
  if (error) return <Box color="text-status-error" fontSize="heading-m">Error: {error}</Box>;
  if (!asset) return <Box color="text-body-secondary">Asset not found</Box>;

  const tags: { key: string; value: string }[] = asset.tags ?? asset.Tags ?? [];
  const describeData = asset.describeData ?? asset.DescribeData ?? asset.rawDescribe ?? null;
  const status = asset.status || asset.Status || "—";

  // Build key-value items from known fields, excluding complex objects
  const skipKeys = new Set(["id", "documentId", "createdAt", "updatedAt", "publishedAt", "tags", "Tags", "describeData", "DescribeData", "rawDescribe", "account", "stack"]);
  const kvItems = Object.entries(asset)
    .filter(([k, v]) => !skipKeys.has(k) && typeof v !== "object")
    .map(([k, v]) => ({ label: k, value: String(v ?? "—") }));

  // Render array-valued json fields (e.g. destinationRegions, modelArns) readably,
  // since kvItems above skips all objects/arrays.
  const arrayFieldItems = Object.entries(asset)
    .filter(([k, v]) => !skipKeys.has(k) && Array.isArray(v))
    .map(([k, v]) => ({
      label: k,
      value: (v as any[]).length > 0 ? (v as any[]).map((x) => String(x)).join(", ") : "—",
    }));

  // Re-sync is only meaningful for registry-backed asset types; FM SDIPs are synced
  // from their own Live Inventory page, so hide the button here.
  const hideResync = assetType === "fm-system-defined-inference-profile";

  // ECS-specific layout
  if (assetType === "ecs-service" && describeData) {
    const svc = typeof describeData === "string" ? JSON.parse(describeData) : describeData;
    const ecsData: EcsServiceData = {
      serviceName: svc.serviceName ?? asset.name,
      clusterName: svc.clusterArn?.split("/").pop() ?? svc.cluster ?? "—",
      status: svc.status ?? status,
      desiredCount: svc.desiredCount ?? 0,
      runningCount: svc.runningCount ?? 0,
      pendingCount: svc.pendingCount,
      taskDefinition: svc.taskDefinition?.split("/").pop() ?? svc.taskDefinition ?? "—",
      launchType: svc.launchType ?? "—",
      createdAt: svc.createdAt,
      tasks: (svc.tasks ?? []).map((t: any) => ({
        taskId: t.taskArn?.split("/").pop() ?? t.taskId,
        lastStatus: t.lastStatus,
        healthStatus: t.healthStatus,
        privateIp: t.privateIp,
        startedAt: t.startedAt,
        containers: t.containers,
      })),
      deployments: (svc.deployments ?? []).map((d: any) => ({
        id: d.id,
        status: d.status,
        desiredCount: d.desiredCount,
        runningCount: d.runningCount,
        pendingCount: d.pendingCount,
        taskDefinition: d.taskDefinition?.split("/").pop() ?? d.taskDefinition,
        createdAt: d.createdAt,
        rolloutState: d.rolloutState,
      })),
      tags: tags.map((t: any) => ({ key: t.key ?? t.Key, value: t.value ?? t.Value })),
    };

    const ecsRegion = asset.region || getDefaultRegion();

    // Distinct container names across all tasks, for the ECS logs container picker.
    const ecsContainers = Array.from(
      new Set(
        (ecsData.tasks ?? []).flatMap((t) => (t.containers ?? []).map((c) => c.name)).filter(Boolean)
      )
    ).map((name) => ({ name }));

    return (
      <SpaceBetween size="l">
        <Flashbar items={flash} />
        <BreadcrumbGroup
          items={[
            { text: "Inventory", href: "/inventory" },
            { text: "ECS Service", href: "/inventory" },
            { text: asset.name || ecsData.serviceName, href: "#" },
          ]}
          onFollow={e => { e.preventDefault(); navigate(e.detail.href); }}
        />
        <Header
          variant="h1"
          description={[
            asset.account && `Account: ${asset.account.Alias} (${asset.account.accountid})`,
            asset.stack && `Stack: ${asset.stack.name}`,
          ].filter(Boolean).join(' | ')}
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Button loading={resyncing} onClick={handleResync}>Re-sync</Button>
              <ButtonDropdown
                items={[
                  { id: "view-ecs-logs", text: "View ECS Logs" },
                  {
                    id: "stop",
                    text: "Stop",
                    disabled: ecsData.desiredCount === 0,
                    disabledReason: ecsData.desiredCount === 0 ? "Service is already stopped (desired count 0)." : undefined,
                  },
                  {
                    id: "start",
                    text: "Start",
                    disabled: ecsData.desiredCount > 0,
                    disabledReason: ecsData.desiredCount > 0 ? "Service is already running (desired count > 0)." : undefined,
                  },
                  ...(ecsData.clusterName && ecsData.clusterName !== "—" && ecsData.serviceName && ecsRegion
                    ? [{
                        id: "aws-console",
                        text: "Open in AWS Console",
                        href: ecsServiceUrl(ecsData.clusterName, ecsData.serviceName, ecsRegion),
                        external: true,
                      }]
                    : []),
                ]}
                onItemClick={({ detail }) => {
                  if (detail.id === "view-ecs-logs") setEcsLogsVisible(true);
                  if (detail.id === "stop") openEcsScale("stop");
                  if (detail.id === "start") openEcsScale("start");
                }}

              >
                Actions
              </ButtonDropdown>
              <ButtonDropdown
                loading={docsLoading}
                items={docItems}
                onItemClick={({ detail }) => {
                  if (detail.id !== "__none__") handleDocSelect(detail.id);
                }}
              >
                Documentation
              </ButtonDropdown>
            </SpaceBetween>
          }
        >
          {asset.name || ecsData.serviceName}
        </Header>
        {asset.stack && (
          <Box><strong>Stack:</strong> <Link to={`/stacks/${asset.stack.documentId}`}>{asset.stack.name}</Link></Box>
        )}
        <EcsServiceDetail service={ecsData} />
        <EcsLogsModal
          visible={ecsLogsVisible}
          onDismiss={() => setEcsLogsVisible(false)}
          ecsServices={[{
            documentId: asset.documentId,
            serviceName: ecsData.serviceName,
            containers: ecsContainers,
          }]}
          accountAlias={asset.account?.Alias ?? null}
          region={asset.region || getDefaultRegion()}
        />
        {(() => {
          // Current desired count: fresh (post-resync) when available, else cached.
          const currentDesired = ecsFreshCounts ? ecsFreshCounts.desiredCount : ecsData.desiredCount;
          // Confirm is blocked while checking, or when the fresh status already
          // satisfies the requested action (nothing to do / would be a no-op).
          const contradicts = !ecsScaleChecking && ecsFreshCounts != null && (
            (ecsScaleAction === "start" && ecsFreshCounts.desiredCount > 0) ||
            (ecsScaleAction === "stop" && ecsFreshCounts.desiredCount === 0)
          );
          const ecsClusterName = ecsData.clusterName;
          const ecsRegion = asset.region || getDefaultRegion();
          return (
            <ConfirmActionModal
              visible={ecsScaleAction !== null}
              onDismiss={() => setEcsScaleAction(null)}
              onConfirm={() => confirmEcsScale(ecsClusterName, ecsData.serviceName, ecsRegion)}
              loading={ecsScaling}
              header={ecsScaleAction === "start" ? "Start ECS service" : "Stop ECS service"}
              confirmLabel={ecsScaleAction === "start" ? "Start" : "Stop"}
              confirmDisabled={ecsScaleChecking || contradicts}
            >
              <SpaceBetween size="s">
                {ecsScaleChecking ? (
                  <Box><Spinner /> Checking current status...</Box>
                ) : (
                  <>
                    <KeyValuePairs
                      columns={3}
                      items={[
                        { label: "Status", value: (ecsFreshCounts?.status ?? ecsData.status) || "—" },
                        { label: "Desired count", value: String(currentDesired) },
                        { label: "Running count", value: String(ecsFreshCounts ? ecsFreshCounts.runningCount : ecsData.runningCount) },
                      ]}
                    />
                    {contradicts ? (
                      <StatusIndicator type="warning">
                        {ecsScaleAction === "start"
                          ? `${ecsData.serviceName} is already running (desired count ${currentDesired}). Nothing to start.`
                          : `${ecsData.serviceName} is already stopped (desired count 0). Nothing to stop.`}
                      </StatusIndicator>
                    ) : (
                      <Box>
                        {ecsScaleAction === "start"
                          ? `This will set the desired task count of ${ecsData.serviceName} to 1. One task will be started.`
                          : `This will set the desired task count of ${ecsData.serviceName} to 0. All running tasks will be stopped.`}
                      </Box>
                    )}
                  </>
                )}
              </SpaceBetween>
            </ConfirmActionModal>
          );
        })()}
        <DocumentationModal
          visible={docModalVisible}
          title={docTitle}
          markdown={docMarkdown}
          loading={docLoading}
          error={docError}
          onDismiss={() => setDocModalVisible(false)}
        />
      </SpaceBetween>
    );
  }

  // EC2-specific layout
  if (assetType === "ec2-instance" && describeData) {
    const raw = typeof describeData === "string" ? JSON.parse(describeData) : describeData;
    const ec2Data: Ec2InstanceData = {
      instanceId: raw.InstanceId ?? asset.resourceId ?? "",
      name: raw.Tags?.find((t: any) => t.Key === "Name")?.Value ?? asset.name ?? "",
      instanceType: raw.InstanceType ?? "",
      state: raw.State?.Name ?? asset.status ?? "",
      privateIp: raw.PrivateIpAddress ?? "",
      publicIp: raw.PublicIpAddress ?? "",
      vpcId: raw.VpcId ?? "",
      subnetId: raw.SubnetId ?? "",
      launchTime: raw.LaunchTime ?? "",
      imageId: raw.ImageId ?? "",
      keyName: raw.KeyName ?? "",
      platform: raw.Platform ?? "linux",
      architecture: raw.Architecture ?? "",
      tags: raw.Tags ?? [],
      securityGroups: raw.SecurityGroups ?? [],
    };

    const accountAlias = asset.account?.Alias;
    const accountCreds = accountAlias ? getAwsCreds(accountAlias) : null;
    const region = asset.region || getDefaultRegion();
    const temporaryOnEnabled = ec2Data.tags.some(
      (t) => t.Key === "ICL_EC2_Temporary_TurnOn" && t.Value === "True"
    );

    const handleEc2InstanceState = async (action: string, instId: string, reg: string, creds: any) => {
      if (!creds) {
        setFlash([{ type: "warning", content: `No AWS credentials for "${accountAlias}". Configure in Settings.`, dismissible: true, onDismiss: () => setFlash([]) }]);
        return;
      }
      const token = getAuthToken();
      if (!token) return;
      const headers: Record<string, string> = {
        "X-AWS-Access-Key-Id": creds.accessKeyId,
        "X-AWS-Secret-Access-Key": creds.secretAccessKey,
        "X-AWS-Session-Token": creds.sessionToken,
      };
      try {
        await apiFetch(`/inventory/live/ec2/${instId}/${action}?region=${reg}`, { method: "POST", headers });
        setFlash([{ type: "success", content: `Instance ${action} initiated`, dismissible: true, onDismiss: () => setFlash([]) }]);
      } catch (e: any) {
        setFlash([{ type: "error", content: e.message, dismissible: true, onDismiss: () => setFlash([]) }]);
      }
    };

    const handleTemporaryOnToggle = async (checked: boolean) => {
      if (!accountCreds) {
        setFlash([{ type: "warning", content: `No AWS credentials for "${accountAlias}". Configure in Settings.`, dismissible: true, onDismiss: () => setFlash([]) }]);
        return;
      }
      const token = getAuthToken();
      if (!token) return;
      const headers: Record<string, string> = {
        "X-AWS-Access-Key-Id": accountCreds.accessKeyId,
        "X-AWS-Secret-Access-Key": accountCreds.secretAccessKey,
        "X-AWS-Session-Token": accountCreds.sessionToken,
      };
      const url = `/inventory/live/ec2/${ec2Data.instanceId}/tags?region=${region}`;
      try {
        if (checked) {
          await apiFetch(url, { method: "POST", headers, body: JSON.stringify({ key: "ICL_EC2_Temporary_TurnOn", value: "True" }) });
        } else {
          await apiFetch(url, { method: "DELETE", headers, body: JSON.stringify({ key: "ICL_EC2_Temporary_TurnOn" }) });
        }
        setFlash([{ type: "success", content: `Temporary On ${checked ? "enabled" : "disabled"}`, dismissible: true, onDismiss: () => setFlash([]) }]);
      } catch (e: any) {
        setFlash([{ type: "error", content: e.message, dismissible: true, onDismiss: () => setFlash([]) }]);
      }
    };

    return (
      <SpaceBetween size="l">
        <Flashbar items={flash} />
        <BreadcrumbGroup
          items={[
            { text: "Inventory", href: "/inventory" },
            { text: "EC2 Instance", href: "/inventory" },
            { text: ec2Data.name || ec2Data.instanceId, href: "#" },
          ]}
          onFollow={e => { e.preventDefault(); navigate(e.detail.href); }}
        />
        <Header
          variant="h1"
          description={asset.account && `Account: ${asset.account.Alias} (${asset.account.accountid})`}
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Button loading={resyncing} onClick={handleResync}>Re-sync</Button>
              <ButtonDropdown
                items={[
                  { id: "temporary-on", text: "Temporary On" },
                  {
                    id: "instance-state",
                    text: "Instance State",
                    items: [
                      { id: "stop", text: "Stop", disabled: ec2Data.state !== "running" },
                      { id: "start", text: "Start", disabled: ec2Data.state !== "stopped" },
                    ],
                  },
                  ...(ec2Data.instanceId && region
                    ? [{
                        id: "aws-console",
                        text: "Open in AWS Console",
                        href: ec2InstanceUrl(ec2Data.instanceId, region),
                        external: true,
                      }]
                    : []),
                ]}
                onItemClick={({ detail }) => {
                  if (detail.id === "temporary-on") setEc2ModalVisible(true);
                  if (detail.id === "stop" || detail.id === "start") handleEc2InstanceState(detail.id, ec2Data.instanceId, region, accountCreds);
                }}
              >
                Actions
              </ButtonDropdown>
              <ButtonDropdown
                loading={docsLoading}
                items={docItems}
                onItemClick={({ detail }) => {
                  if (detail.id !== "__none__") handleDocSelect(detail.id);
                }}
              >
                Documentation
              </ButtonDropdown>
            </SpaceBetween>
          }
        >
          {ec2Data.name || ec2Data.instanceId}
        </Header>
        {asset.stack && (
          <Box><strong>Stack:</strong> <Link to={`/stacks/${asset.stack.documentId}`}>{asset.stack.name}</Link></Box>
        )}
        <Ec2InstanceDetail instance={ec2Data} />
        <Modal
          visible={ec2ModalVisible}
          onDismiss={() => setEc2ModalVisible(false)}
          header="Temporary On"
          footer={<Box float="right"><Button variant="primary" onClick={() => setEc2ModalVisible(false)}>Close</Button></Box>}
        >
          <SpaceBetween size="m">
            <div><Box variant="awsui-key-label">Instance ID</Box><div>{ec2Data.instanceId}</div></div>
            <div><Box variant="awsui-key-label">Name</Box><div>{ec2Data.name}</div></div>
            <Box variant="p">
              The 'Temporary On' feature marks this instance for automatic shutdown to save costs.
              When enabled, the tag ICL_EC2_Temporary_TurnOn=true is added to the instance.
              An automated process will turn off instances with this tag according to a schedule.
            </Box>
            <Toggle
              checked={temporaryOnEnabled}
              onChange={({ detail }) => handleTemporaryOnToggle(detail.checked)}
              disabled={!accountCreds}
            >
              {temporaryOnEnabled ? "Enabled" : "Disabled"}
            </Toggle>
            {!accountCreds && <StatusIndicator type="warning">AWS credentials not configured for this account</StatusIndicator>}
          </SpaceBetween>
        </Modal>
        <DocumentationModal
          visible={docModalVisible}
          title={docTitle}
          markdown={docMarkdown}
          loading={docLoading}
          error={docError}
          onDismiss={() => setDocModalVisible(false)}
        />
      </SpaceBetween>
    );
  }

  // Generic fallback block: derive a valid console URL from the cached asset per type.
  // Render nothing when the required identifier or region is missing.
  const genericRegion = asset.region || getDefaultRegion();
  let consoleUrl: string | null = null;
  if (genericRegion) {
    if (assetType === "lambda-function") {
      const functionName = asset.functionName || asset.resourceId;
      if (functionName) consoleUrl = lambdaFunctionUrl(functionName, genericRegion);
    } else if (assetType === "rds-instance") {
      const dbInstanceId = asset.dbInstanceIdentifier || asset.resourceId;
      if (dbInstanceId) consoleUrl = rdsInstanceUrl(dbInstanceId, genericRegion);
    } else if (assetType === "api-gateway") {
      const apiId = asset.apiId || asset.resourceId;
      if (apiId) consoleUrl = apiGatewayUrl(apiId, genericRegion);
      else if (asset.apiName) consoleUrl = `https://${genericRegion}.console.aws.amazon.com/apigateway/main/apis?region=${encodeURIComponent(genericRegion)}`;
    } else if (assetType === "dynamodb-table") {
      const tableName = asset.tableName || asset.resourceId;
      if (tableName) consoleUrl = dynamoDbTableUrl(tableName, genericRegion);
    }
  }

  return (
    <SpaceBetween size="l">
      <Flashbar items={flash} />
      <BreadcrumbGroup
        items={[
          { text: "Inventory", href: "/inventory" },
          { text: TYPE_LABELS[assetType!] ?? assetType!, href: "#" },
        ]}
        onFollow={e => { e.preventDefault(); navigate(e.detail.href); }}
      />
      <Header
        variant="h1"
        description={<StatusIndicator type={statusType(status)}>{status}</StatusIndicator>}
        actions={
          <SpaceBetween direction="horizontal" size="xs">
            {consoleUrl && (
              <Button iconName="external" href={consoleUrl} target="_blank">
                Open in AWS Console
              </Button>
            )}
            {!hideResync && <Button loading={resyncing} onClick={handleResync}>Re-sync</Button>}
          </SpaceBetween>
        }
      >
        {asset.name || asset.Name || asset.arn || asset.ARN || "Asset Detail"}
      </Header>
      <ColumnLayout columns={3}>
        <KeyValuePairs items={[
          ...kvItems,
          ...arrayFieldItems,
          { label: "Account", value: asset.account ? `${asset.account.Alias} (${asset.account.accountid})` : "—" },
          {
            label: "Stack",
            value: asset.stack
              ? <Link to={`/stacks/${asset.stack.documentId}`}>{asset.stack.name}</Link>
              : "—",
          },
        ]} />
      </ColumnLayout>
      {tags.length > 0 && (
        <Table
          variant="embedded"
          header={<Header>Tags</Header>}
          items={tags}
          columnDefinitions={[
            { id: "key", header: "Key", cell: t => t.key || (t as any).Key },
            { id: "value", header: "Value", cell: t => t.value || (t as any).Value },
          ]}
        />
      )}
      {describeData && (
        <ExpandableSection headerText="Raw Describe Data">
          <Box variant="code">
            <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
              {typeof describeData === "string" ? describeData : JSON.stringify(describeData, null, 2)}
            </pre>
          </Box>
        </ExpandableSection>
      )}
    </SpaceBetween>
  );
}
